import { useEffect, useReducer, useRef, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Info } from 'lucide-react';
import { cn } from 'lib/utils';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from 'components/ui/drawer';
import { REFUSAL_REASON_CODES, type RefusalReasonCode } from 'api/interventionRequestsApi';
import { refusalReasonLabel } from 'pages/AdminIssues/components/upcomingAssignment';

/**
 * The refusal sheet — a bottom sheet, the native idiom on a phone, and it saves a route the
 * L'Oréal shells could not reach anyway.
 *
 * A code is required (D25) and the free precision never is: refusing the refusal pushes a
 * technician into not answering at all, which is the one outcome with no signal. `other` with no
 * text is a valid answer.
 */

const LABEL = 'text-[13px] font-medium text-zinc-900 dark:text-zinc-100';

interface Form {
  code: RefusalReasonCode | null;
  reason: string;
}

type Action = { type: 'SET_CODE'; code: RefusalReasonCode } | { type: 'SET_REASON'; reason: string } | { type: 'RESET' };

const EMPTY: Form = { code: null, reason: '' };

/** Both axes, because the group is vertical on screen and a reader may try either (ARIA). */
const ARROW_STEP: Record<string, number> = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 };

const formReducer = (state: Form, action: Action): Form => {
  switch (action.type) {
    case 'SET_CODE':
      return { ...state, code: action.code };
    case 'SET_REASON':
      return { ...state, reason: action.reason };
    default:
      return EMPTY;
  }
};

interface DeclineSheetProps {
  open: boolean;
  pending: boolean;
  /** Already a sentence: the caller owns the three families of failure, this only renders one. */
  error: string | null;
  onClose: () => void;
  onConfirm: (code: RefusalReasonCode, reason: string | null) => void;
}

const DeclineSheet = ({ open, pending, error, onClose, onConfirm }: DeclineSheetProps) => {
  const { t } = useTranslation();
  const [form, dispatch] = useReducer(formReducer, EMPTY);
  const options = useRef<(HTMLButtonElement | null)[]>([]);

  // A reopened sheet is a new refusal, never the previous one's answer.
  useEffect(() => {
    if (open) dispatch({ type: 'RESET' });
  }, [open]);

  /**
   * The ARIA radiogroup pattern the `role="radio"` markup was only claiming: one tab stop for the
   * whole group, and arrows that move focus **and** select, wrapping at both ends. Space and Enter
   * need no handling — a `<button>` already fires its click.
   */
  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const count = REFUSAL_REASON_CODES.length;
    const step = ARROW_STEP[event.key];
    const next =
      step !== undefined ? (index + step + count) % count
      : event.key === 'Home' ? 0
      : event.key === 'End' ? count - 1
      : null;
    if (next === null) return;
    event.preventDefault();
    dispatch({ type: 'SET_CODE', code: REFUSAL_REASON_CODES[next] });
    options.current[next]?.focus();
  };

  // The tab stop is the selected option, or the first one while nothing is picked.
  const tabStop = form.code ? REFUSAL_REASON_CODES.indexOf(form.code) : 0;

  const submit = () => {
    if (!form.code || pending) return;
    // Empty text normalises to null server-side; sending `''` would read as "a reason was given,
    // and it was blank".
    onConfirm(form.code, form.reason.trim() || null);
  };

  return (
    <Drawer open={open} onOpenChange={(next) => !next && onClose()}>
      {/*
        Three panes, and the middle one is the only one that scrolls.
        `overflow-y-auto` on `DrawerContent` itself is inert: vaul stamps it with
        `[data-vaul-drawer] { touch-action: none }` and owns every touch on it, so on a 360×640 dp
        phone the sheet clips at the WebView's bottom edge and the actions below the fold are
        unreachable. `touch-action` is not inherited, so a plain child scrolls normally — and the
        actions sit outside it, which keeps them on screen at any height.
      */}
      <DrawerContent className="mx-auto max-h-[92dvh] w-full overflow-hidden sm:max-w-lg">
        <DrawerHeader className="shrink-0 text-left">
          <DrawerTitle>{t('Decline this request')}</DrawerTitle>
          <DrawerDescription>{t('Tell us why you cannot take this intervention.')}</DrawerDescription>
        </DrawerHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-5 pb-4">
          {/* Plain buttons and not a `Select`: a Radix `Select` mounts empty inside a form, and
              the design draws this list of tinted pills anyway. */}
          <div className="flex flex-col gap-2" role="radiogroup" aria-label={t('Reason')}>
            {REFUSAL_REASON_CODES.map((code, index) => (
              <button
                key={code}
                type="button"
                role="radio"
                aria-checked={form.code === code}
                ref={(node) => {
                  options.current[index] = node;
                }}
                tabIndex={index === tabStop ? 0 : -1}
                onKeyDown={(event) => onKeyDown(event, index)}
                onClick={() => dispatch({ type: 'SET_CODE', code })}
                className={cn(
                  'flex min-h-11 items-center justify-between gap-3 rounded-xl border px-4 py-2.5 text-left text-[14px]',
                  form.code === code
                    ? 'border-rose-300 bg-rose-50 font-medium text-rose-800 dark:border-rose-800 dark:bg-rose-950 dark:text-rose-200'
                    : 'border-zinc-200 text-zinc-700 dark:border-zinc-800 dark:text-zinc-300'
                )}
              >
                {refusalReasonLabel(t, code)}
                {form.code === code && <Check className="h-4 w-4 shrink-0" aria-hidden="true" />}
              </button>
            ))}
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="decline-reason" className={LABEL}>
              {t('Add a precision')}{' '}
              <span className="font-normal text-zinc-400 dark:text-zinc-500">({t('optional')})</span>
            </label>
            {/* The shared `Input`, as the sibling unavailability sheet does for its own optional
                500-character reason: `Textarea` is untyped JS and unusable from TypeScript. */}
            <Input
              id="decline-reason"
              value={form.reason}
              maxLength={500}
              onChange={(event) => dispatch({ type: 'SET_REASON', reason: event.target.value })}
              className="h-11 rounded-xl text-[15px]"
            />
          </div>

          {error && (
            <p className="text-[13px] leading-snug text-rose-600 dark:text-rose-400" role="alert">
              {error}
            </p>
          )}

          <p className="flex gap-2.5 rounded-xl border border-zinc-200 p-3 text-[12.5px] leading-relaxed text-zinc-500 dark:border-zinc-800 dark:text-zinc-400">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-zinc-400" aria-hidden="true" />
            {t('This reason is visible only to the GPS team.')}
          </p>
        </div>

        {/* Outside the scroll region: the two actions stay on screen whatever the sheet's height. */}
        <div className="flex shrink-0 gap-2.5 border-t border-zinc-200 px-5 pb-8 pt-4 dark:border-zinc-800">
          <Button type="button" variant="outline" className="h-11 px-5" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button type="button" className="h-11 flex-1" disabled={!form.code || pending} onClick={submit}>
            {t('Confirm the refusal')}
          </Button>
        </div>
      </DrawerContent>
    </Drawer>
  );
};

export default DeclineSheet;
